import { useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CirclePlay, Expand, Minimize, Play, Sparkles } from "lucide-react";

type TrainingVideo = {
  id?: number;
  slug: string;
  title: string;
  description?: string | null;
  videoUrl: string;
  thumbnailUrl?: string | null;
  placement?: string | null;
  companyContext?: string | null;
};

type TrainingVideoPlayerProps = {
  video: TrainingVideo;
  eyebrow?: string;
  ctaLabel?: string;
  compact?: boolean;
  className?: string;
  descriptionOverride?: string;
  triggerVariant?: "button" | "thumbnail";
  surface?: "light" | "dark";
  layout?: "stacked" | "split";
};

function getContextLabel(context?: string | null) {
  if (context === "group_company") return "Grupo econômico";
  if (context === "single_company") return "Empresa";
  return "Treinamento";
}

function getCoverTone(video: TrainingVideo) {
  if (video.companyContext === "group_company") {
    return "from-[#0d2b54] via-[#133a6c] to-[#09172c]";
  }

  if (video.companyContext === "single_company") {
    return "from-[#0f355f] via-[#164776] to-[#0a1c34]";
  }

  return "from-[#112d52] via-[#194b7f] to-[#0a1a31]";
}

export default function TrainingVideoPlayer({
  video,
  eyebrow = "Treinamento",
  ctaLabel = "Assistir",
  compact = false,
  className,
  descriptionOverride,
  triggerVariant = "thumbnail",
  surface = "light",
  layout = "stacked",
}: TrainingVideoPlayerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpandedView, setIsExpandedView] = useState(false);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const contextLabel = useMemo(() => getContextLabel(video.companyContext), [video.companyContext]);
  const description =
    descriptionOverride || video.description || "Assista a este treinamento diretamente no portal, sem sair da jornada atual.";
  const isDarkSurface = surface === "dark";
  const isSplitLayout = layout === "split";
  const coverTone = getCoverTone(video);
  const hasThumbnail = Boolean(video.thumbnailUrl);
  const cardClassName = isDarkSurface
    ? "overflow-hidden border border-white/10 bg-white/5 shadow-none backdrop-blur-sm"
    : "overflow-hidden border border-slate-200 bg-white shadow-sm";
  const titleClassName = isDarkSurface ? "text-white" : "text-slate-950";
  const descriptionClassName = isDarkSurface ? "text-white/72" : "text-slate-600";
  const secondaryBadgeClassName = isDarkSurface
    ? "rounded-full border border-white/10 bg-white/10 text-white/90"
    : "rounded-full bg-slate-100 text-slate-700";
  const outlineBadgeClassName = isDarkSurface
    ? "rounded-full border-white/15 text-white/80"
    : "rounded-full";

  const handleToggleExpandedView = () => {
    setIsExpandedView((current) => !current);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        setIsOpen(nextOpen);
        if (!nextOpen) {
          setIsExpandedView(false);
        }
      }}
    >
      <Card className={`${cardClassName} ${className ?? ""}`}>
        <div className={compact ? "p-4" : "p-5 lg:p-6"}>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className={secondaryBadgeClassName}>
              {eyebrow}
            </Badge>
            <Badge variant="outline" className={outlineBadgeClassName}>
              {contextLabel}
            </Badge>
          </div>

          <div className={`mt-4 ${isSplitLayout ? "grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-center" : "space-y-4"}`}>
            <div>
              <h3 className={`text-lg font-semibold lg:text-xl ${titleClassName}`}>{video.title}</h3>
              <p className={`mt-2 text-sm leading-6 ${descriptionClassName}`}>{description}</p>
            </div>

            <DialogTrigger asChild>
              {triggerVariant === "button" ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#0c3561] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0f457d]"
                >
                  <CirclePlay className="h-4 w-4" />
                  {ctaLabel}
                </button>
              ) : (
                <button
                  type="button"
                  aria-label={`${ctaLabel} ${video.title}`}
                  className={`group relative block w-full overflow-hidden rounded-[1.5rem] border text-left transition hover:-translate-y-0.5 ${
                    isDarkSurface ? "border-white/10" : "border-slate-200"
                  } ${isSplitLayout ? "max-w-[360px] justify-self-end" : ""}`}
                >
                  <div className={`relative ${isSplitLayout ? "aspect-[16/8.8]" : "aspect-[16/7]"} overflow-hidden bg-gradient-to-br ${coverTone}`}>
                    {hasThumbnail ? (
                      <img
                        src={video.thumbnailUrl ?? undefined}
                        alt={`Capa do vídeo ${video.title}`}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    ) : null}
                    <div className={`absolute inset-0 ${hasThumbnail ? "bg-[linear-gradient(135deg,rgba(3,11,24,0.18),rgba(3,11,24,0.08)_38%,rgba(3,11,24,0.34)_100%)]" : "bg-[radial-gradient(circle_at_top_right,rgba(192,255,0,0.22),transparent_28%)]"}`} />
                    <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.08),transparent_42%,rgba(3,20,38,0.36))]" />
                    <div className={`relative flex h-full flex-col justify-between ${isSplitLayout ? "p-3.5" : "p-4 sm:p-5"}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="inline-flex w-fit items-center gap-2 rounded-full bg-white/12 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/90 backdrop-blur-sm">
                          <Sparkles className="h-3.5 w-3.5 text-[#C0FF00]" />
                          Vídeo de treinamento
                        </div>
                        <div className={`flex items-center justify-center rounded-full bg-white/12 text-white shadow-lg ring-1 ring-white/15 backdrop-blur-sm transition group-hover:scale-105 group-hover:bg-white/18 ${isSplitLayout ? "h-10 w-10" : "h-12 w-12"}`}>
                          <Play className={`ml-0.5 fill-current text-[#C0FF00] ${isSplitLayout ? "h-4.5 w-4.5" : "h-5 w-5"}`} />
                        </div>
                      </div>

                      <div className={`space-y-2 ${isSplitLayout ? "max-w-full" : "max-w-[85%]"}`}>
                        <p className={`${isSplitLayout ? "text-xs" : "text-sm"} font-medium text-white/70`}>{ctaLabel}</p>
                        <p className={`${isSplitLayout ? "text-sm sm:text-base" : "text-base sm:text-lg"} font-semibold leading-snug text-white`}>{video.title}</p>
                      </div>
                    </div>
                  </div>
                </button>
              )}
            </DialogTrigger>
          </div>
        </div>
      </Card>

      <DialogContent
        className={isExpandedView
          ? "h-[100dvh] w-[100dvw] max-w-none border-0 bg-slate-950 p-0 text-white sm:rounded-none"
          : "max-w-5xl border-slate-200 bg-slate-950 p-0 text-white"}
        showCloseButton
      >
        <DialogHeader className={`border-b border-white/10 text-left ${isExpandedView ? "px-5 py-4 sm:px-6" : "px-6 py-5"}`}>
          <DialogTitle className="text-xl text-white">{video.title}</DialogTitle>
          <DialogDescription className="text-slate-300">
            {descriptionOverride || video.description || "Treinamento reproduzido no próprio portal."}
          </DialogDescription>
        </DialogHeader>
        <div className={isExpandedView ? "flex h-[calc(100dvh-88px)] flex-col p-3 sm:p-4" : "p-4 sm:p-6"}>
          <div className="mb-3 flex items-center justify-end">
            <button
              type="button"
              onClick={handleToggleExpandedView}
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-white/10"
            >
              {isExpandedView ? <Minimize className="h-4 w-4" /> : <Expand className="h-4 w-4" />}
              {isExpandedView ? "Sair da tela cheia" : "Tela cheia"}
            </button>
          </div>
          <div className={`overflow-hidden border border-white/10 bg-black shadow-2xl ${isExpandedView ? "flex-1 rounded-[1.25rem]" : "rounded-[1.5rem]"}`}>
            <video
              key={video.videoUrl}
              ref={videoElementRef}
              controls
              playsInline
              preload="metadata"
              className={isExpandedView ? "h-full w-full bg-black object-contain" : "aspect-video w-full bg-black"}
              poster={video.thumbnailUrl ?? undefined}
              src={video.videoUrl}
            >
              Seu navegador não conseguiu reproduzir este vídeo.
            </video>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
